/**
 * BACKGROUND JOBS — long analyses that run while the reader keeps working.
 *
 * One provider for the whole signed-in app, and the only thing in it that
 * polls. It follows every job the signed-in user started (getTriggeredEvents,
 * last 24 hours), and when one finishes it refetches what that job made stale
 * and says so in a toast — on whichever screen is open. Screens only ask it
 * questions ("is this portfolio being analysed?") and hand it work to follow.
 *
 * What each job is called and what it refetches is config/jobTypes.js.
 *
 * Polling:
 *   - once on load, so a refresh or a new tab picks up work already running;
 *   - then while anything is WAITING or RUNNING, and for 30s after start(),
 *     because the rows a call queues can land a moment after it returns;
 *   - every 10s, easing to 30s once a wait passes two minutes;
 *   - paused while the tab is hidden, and stopped when nothing is active.
 *
 * The jobs are the ADVISOR's own — nothing here is sent on a client's behalf —
 * so the toasts follow the advisor across every client's screens.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import { TRIGGERED_EVENTS } from './queries';
import { CANCEL_TRIGGERED_EVENT, RUN_PORTFOLIO_ANALYTICS } from './mutations';
import { asClient } from './clientScope';
import { ANALYSIS_KIND, JOB_TYPES, isOfType, jobTypeOf } from '../config/jobTypes';

export const ACTIVE_STATES = new Set(['WAITING', 'RUNNING']);
export const isActiveJob = (job) => ACTIVE_STATES.has(job.state) && !job.cancelled;
/** A run's identity across polls. */
export const jobKey = (job) => `${job.kind}:${job.key}`;

const FAST_MS = 10000;
const SLOW_MS = 30000;
const BACKOFF_AFTER_MS = 120000;
const WATCH_MS = 30000;
/* How long one expected type is waited for — its own, where it names one. */
const watchMsOf = (type) => JOB_TYPES[type]?.watchMs ?? WATCH_MS;
/* A row enqueued this long before start() still counts as the one started —
   the browser's clock and the server's are not the same clock. */
const CLOCK_SLACK_MS = 60000;
const TOAST_MS = 12000;
const EMPTY = [];

/**
 * What changed between two polls: the runs that finished or failed.
 *
 * A run counts when it was seen WAITING or RUNNING before, or when it is new
 * since the last poll — a fast job can be queued and finish between two polls.
 * On the first poll nothing is new: that is the last 24 hours as they stood
 * when the app opened, and toasting them would replay a day of old news.
 */
export function diffJobs(prev, rows, { initial = false } = {}) {
  const events = [];
  rows.forEach((row) => {
    const before = prev.get(jobKey(row));
    if (before === row.state) return;
    const followed = ACTIVE_STATES.has(before) || (before === undefined && !initial);
    if (!followed) return;
    if (row.state === 'DONE') events.push({ type: 'done', row });
    else if (row.state === 'FAILED') events.push({ type: 'failed', row });
  });
  return events;
}

/** The root fields an operation reads — what evicting would take from under it. */
const rootFieldsOf = (doc) => (doc?.definitions ?? [])
  .filter((d) => d.kind === 'OperationDefinition')
  .flatMap((d) => d.selectionSet.selections)
  .filter((s) => s.kind === 'Field')
  .map((s) => s.name.value);

/* ── what the app knows about a subject that the job row does not carry ── */

/* The client a portfolio belongs to and what it is called, remembered when
   work is started so a toast can name it and a check can be asked on the
   client's behalf — including after a reload. Browser storage, because it is
   only a convenience: without it the toast names the symbol instead. */
const SUBJECTS_KEY = 'ki.backgroundJobs.subjects';
const readSubjects = () => {
  try {
    return JSON.parse(window.localStorage.getItem(SUBJECTS_KEY) || '{}') || {};
  } catch {
    return {};
  }
};
const rememberSubject = (subject, meta) => {
  if (!subject) return;
  try {
    const all = readSubjects();
    const cutoff = Date.now() - 24 * 3600 * 1000;
    Object.keys(all).forEach((k) => { if ((all[k]?.at ?? 0) < cutoff) delete all[k]; });
    all[subject] = { ...all[subject], ...meta, at: Date.now() };
    window.localStorage.setItem(SUBJECTS_KEY, JSON.stringify(all));
  } catch {
    /* storage refused — the toast falls back to the symbol */
  }
};

const Ctx = createContext(null);

export function BackgroundJobsProvider({ children }) {
  const client = useApolloClient();
  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);

  const [jobs, setJobs] = useState(EMPTY);
  const [toasts, setToasts] = useState(EMPTY);
  /* Work handed to start() whose row has not appeared yet, so the screen that
     started it reads it as active from the click, not from the first poll. */
  const [starting, setStarting] = useState(EMPTY);

  const jobsRef = useRef(EMPTY);
  const prev = useRef(null);
  const timer = useRef(null);
  const watchUntil = useRef(0);
  const pollingSince = useRef(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const pollRef = useRef(null);

  const dismiss = useCallback((id) => setToasts((all) => all.filter((t) => t.id !== id)), []);
  const toast = useCallback((t) => {
    const id = `${Date.now()}:${Math.random()}`;
    setToasts((all) => [...all.slice(-3), { ...t, id }]);
    setTimeout(() => dismiss(id), TOAST_MS);
  }, [dismiss]);

  const schedule = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    if (!alive.current || document.visibilityState === 'hidden') return;
    const now = Date.now();
    const needed = jobsRef.current.some(isActiveJob) || now < watchUntil.current;
    if (!needed) {
      pollingSince.current = null;
      return;
    }
    if (pollingSince.current == null) pollingSince.current = now;
    const wait = now - pollingSince.current > BACKOFF_AFTER_MS ? SLOW_MS : FAST_MS;
    timer.current = setTimeout(() => pollRef.current?.(), wait);
  }, []);

  /* Re-ask what the job made stale. The named queries on screen are re-run in
     place — they keep showing the last figures until the new ones land. The
     job's fields that NO query on screen reads are dropped from the cache, so
     a screen opened later reads fresh instead of what was cached before the
     job ran. Evicting a field a mounted query reads would blank that screen
     mid-refetch, which is why those are left to the refetch. Names not on
     screen are left out of `include` — Apollo warns about each one. */
  const refresh = useCallback(async (type) => {
    const active = [...client.getObservableQueries('active').values()];
    const onScreen = new Set(active.map((q) => q.queryName).filter(Boolean));
    const readOnScreen = new Set(active.flatMap((q) => rootFieldsOf(q.query)));
    const include = type.queriesToRefetch.filter((n) => onScreen.has(n));
    const evict = type.evict.filter((f) => !readOnScreen.has(f));
    if (evict.length) {
      evict.forEach((fieldName) => client.cache.evict({ id: 'ROOT_QUERY', fieldName }));
      client.cache.gc();
    }
    if (include.length) await client.refetchQueries({ include });
  }, [client]);

  /* Whether a finished job actually produced anything. Asked on the client's
     behalf when the portfolio is known to be theirs; when it is not known
     (started elsewhere, storage cleared), the check is skipped rather than
     risk calling a real review missing. */
  const produced = useCallback(async (type, row) => {
    if (!type.verify) return true;
    const meta = readSubjects()[row.subject];
    if (!meta || !('clientUserId' in meta)) return true;
    try {
      const { data } = await client.query({
        query: type.verify.query,
        variables: type.verify.variables(row.subject),
        context: asClient(meta.clientUserId),
        fetchPolicy: 'network-only',
      });
      return !type.verify.isEmpty(data);
    } catch {
      return true;
    }
  }, [client]);

  /* Work known to be on its way whose row has not appeared: counted as active
     for `subject` and polled for, for as long as its type is waited for. */
  const expect = useCallback((subject, types, at = Date.now()) => {
    if (subject && types.length) {
      const hints = types.map((type) => ({ subject, type, at }));
      setStarting((all) => [...all, ...hints]);
      hints.forEach((hint) => {
        setTimeout(() => setStarting((all) => all.filter((s) => s !== hint)), watchMsOf(hint.type));
      });
    }
    watchUntil.current = Math.max(watchUntil.current, Date.now() + Math.max(WATCH_MS, ...types.map(watchMsOf)));
  }, []);

  const start = useCallback(async (promise, { subject = null, type = ANALYSIS_KIND, clientUserId, name } = {}) => {
    const at = Date.now();
    const result = await promise;
    if (result && result.success === false) return result;
    const symbol = subject ?? result?.portfolio_symbol ?? null;
    rememberSubject(symbol, {
      ...(clientUserId !== undefined ? { clientUserId } : {}),
      ...(name ? { name } : {}),
    });
    expect(symbol, [type], at);
    pollRef.current?.();
    return result;
  }, [expect]);

  const retry = useCallback((row) => start(
    client.mutate({
      mutation: RUN_PORTFOLIO_ANALYTICS,
      variables: { securitySymbol: row.subject, queue: true },
      fetchPolicy: 'no-cache',
    }).then(({ data }) => data?.runPortfolioAnalytics ?? null),
    { subject: row.subject },
  ).catch((e) => toast({ tone: 'failed', text: `Retry failed — ${e.message}` })), [client, start, toast]);

  const onEvent = useCallback(async ({ type: what, row }) => {
    const type = jobTypeOf(row);
    const meta = readSubjects()[row.subject] ?? {};
    const portfolio = meta.name || row.subject;
    const failed = () => toast({
      tone: 'failed',
      text: `${type.label} failed${row.subject ? ` for ${portfolio}` : ''}`,
      subject: row.subject,
      onRetry: row.subject ? () => retry(row) : null,
    });
    if (what === 'failed') {
      failed();
      return;
    }
    /* Keep watching for what this job queues as it finishes. */
    expect(row.subject, type.queues);
    try {
      await refresh(type);
    } catch {
      /* the toast still goes: the data is there, only this screen's copy is old */
    }
    if (!(await produced(type, row))) {
      failed();
      return;
    }
    toast({
      tone: 'done',
      text: row.subject ? `${type.label} ready for ${portfolio}` : `${type.label} ready`,
      subject: row.subject,
    });
  }, [expect, refresh, produced, retry, toast]);

  const poll = useCallback(async () => {
    if (inFlight.current || !alive.current) return;
    inFlight.current = true;
    try {
      const { data } = await client.query({
        query: TRIGGERED_EVENTS,
        variables: { subject: null },
        fetchPolicy: 'network-only',
      });
      const rows = data?.getTriggeredEvents ?? EMPTY;
      const events = diffJobs(prev.current ?? new Map(), rows, { initial: prev.current == null });
      prev.current = new Map(rows.map((r) => [jobKey(r), r.state]));
      jobsRef.current = rows;
      setJobs(rows);
      /* A started job whose row is now in the list no longer needs a hint. */
      setStarting((all) => all.filter((s) => !rows.some((r) => r.subject === s.subject
        && isOfType(r, s.type)
        && (Date.parse(r.enqueuedAt ?? '') || Infinity) >= s.at - CLOCK_SLACK_MS)));
      events.forEach(onEvent);
    } catch {
      /* a failed poll keeps the last list; the next tick asks again */
    } finally {
      inFlight.current = false;
      schedule();
    }
  }, [client, onEvent, schedule]);
  pollRef.current = poll;

  /* Once on load (and on sign-in), then only as schedule() decides. */
  useEffect(() => {
    alive.current = true;
    if (isLoggedIn) pollRef.current?.();
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, [isLoggedIn]);

  /* Hidden tabs do not poll; coming back asks at once if anything is due. */
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(timer.current);
        timer.current = null;
      } else if (jobsRef.current.some(isActiveJob) || Date.now() < watchUntil.current) {
        pollRef.current?.();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const cancel = useCallback(async (job) => {
    const { data } = await client.mutate({
      mutation: CANCEL_TRIGGERED_EVENT,
      variables: { kind: job.kind, key: job.key },
      fetchPolicy: 'no-cache',
    });
    const result = data?.cancelTriggeredEvent;
    if (result?.error) toast({ tone: 'failed', text: result.error });
    pollRef.current?.();
    return result;
  }, [client, toast]);

  const value = useMemo(() => {
    const activeJobs = jobs.filter(isActiveJob);
    const isActive = (subject, type) => Boolean(subject) && (
      activeJobs.some((j) => j.subject === subject && isOfType(j, type))
      || starting.some((s) => s.subject === subject && (!type || s.type === type))
    );
    return {
      jobs, activeJobs, isActive, start, cancel, toasts, dismiss,
    };
  }, [jobs, starting, start, cancel, toasts, dismiss]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/* Outside the provider (the marketing and auth screens) nothing is tracked,
   and a screen asking gets a quiet answer rather than a crash. */
const IDLE = {
  jobs: EMPTY,
  activeJobs: EMPTY,
  isActive: () => false,
  start: (promise) => promise,
  cancel: async () => null,
  toasts: EMPTY,
  dismiss: () => {},
};

/**
 * The signed-in user's background jobs.
 *
 *   jobs        every run from the last 24 hours, newest first
 *   activeJobs  those WAITING or RUNNING
 *   isActive(subject, type?)  is work running for this portfolio — of a kind
 *               ("batch:portfolio_analytics") or event type, or any
 *   start(promise, { subject?, type?, clientUserId?, name? })
 *               follow the work a call just queued; resolves to its result
 *   cancel(job) take a WAITING job off the queue
 */
export const useBackgroundJobs = () => useContext(Ctx) ?? IDLE;
